export const API_STRUCTURE = [
  {
    id: "getting-started",
    title: "Getting Started",
    articles: [
      {
        id: "overview",
        title: "Overview",
        description: `The Flare API lets you programmatically manage your video library, automate uploads, customize embeds, and retrieve analytics data.

**Base URL**

All API requests should be made to:

- \`https://flare.link\`

**Authentication**

Include your API key as a Bearer token in the Authorization header:

- \`Authorization: Bearer flr_pat_...\`

**Available Scopes**

API keys can be configured with the following permission scopes:

- **videos:read** - List and fetch video details
- **videos:write** - Create, update, and delete videos
- **uploads:write** - Generate presigned upload URLs
- **embeds:write** - Modify embed settings and get embed codes`,
        examples: {
          curl: `curl https://flare.link/api/videos \\
  -H "Authorization: Bearer flr_pat_..."`,
          node: `const res = await fetch("https://flare.link/api/videos", {
  headers: {
    Authorization: "Bearer flr_pat_..."
  }
});
const data = await res.json();`,
          python: `import requests

res = requests.get(
  "https://flare.link/api/videos",
  headers={"Authorization": "Bearer flr_pat_..."}
)
print(res.json())`,
        },
        response: `{
  "success": true,
  "videos": []
}`,
        method: "GET",
        path: "/api/videos",
        scopes: ["videos:read"],
      },
      {
        id: "rate-limits",
        title: "Rate limits",
        description: `All API endpoints are rate limited to ensure fair usage and platform stability.

**Default Limits**

- **120 requests** per minute per API key
- Rate limits reset every 60 seconds

**Response Headers**

Every response includes rate limit information:

- **X-RateLimit-Limit** - Maximum requests allowed per window
- **X-RateLimit-Remaining** - Requests remaining in current window
- **X-RateLimit-Reset** - Unix timestamp when the window resets

**Rate Limit Exceeded**

When you exceed the limit, the API returns a \`429 Too Many Requests\` response. Wait until the reset time before retrying.

**Need Higher Limits?**

Contact support at support@flare.link for enterprise rate limits.`,
        examples: {
          curl: `# Rate limit headers are included in every response
curl -i https://flare.link/api/videos \\
  -H "Authorization: Bearer flr_pat_..."

# Response headers:
# X-RateLimit-Limit: 120
# X-RateLimit-Remaining: 119
# X-RateLimit-Reset: 1706400000`,
          node: `const res = await fetch("https://flare.link/api/videos", {
  headers: { Authorization: "Bearer flr_pat_..." }
});

// Check rate limit headers
const limit = res.headers.get("X-RateLimit-Limit");
const remaining = res.headers.get("X-RateLimit-Remaining");
const reset = res.headers.get("X-RateLimit-Reset");

if (res.status === 429) {
  const waitMs = (reset * 1000) - Date.now();
  await new Promise(r => setTimeout(r, waitMs));
  // Retry request...
}`,
          python: `import requests
import time

res = requests.get(
  "https://flare.link/api/videos",
  headers={"Authorization": "Bearer flr_pat_..."}
)

# Check rate limit headers
limit = res.headers.get("X-RateLimit-Limit")
remaining = res.headers.get("X-RateLimit-Remaining")
reset = int(res.headers.get("X-RateLimit-Reset", 0))

if res.status_code == 429:
    wait_time = reset - time.time()
    time.sleep(max(0, wait_time))
    # Retry request...`,
        },
        response: `# 429 Too Many Requests
{
  "error": "Rate limit exceeded",
  "retryAfter": 42
}`,
        method: "GET",
        path: "/api/*",
        scopes: [],
      },
    ],
  },
  {
    id: "authentication",
    title: "Authentication",
    articles: [
      {
        id: "create-token",
        title: "Create an API key",
        description: `API keys provide secure access to the Flare API. Each key can be configured with specific scopes to limit access.

**Creating a Key**

- Navigate to **Profile** → **API Keys** in the Flare dashboard
- Click **Create API Key**
- Enter a descriptive name (e.g., "Production Server", "CI/CD Pipeline")
- Select the required scopes for your use case
- Click **Create** and copy your key immediately

**Security Best Practices**

- **Keys are shown only once** - Store them securely immediately after creation
- **Use environment variables** - Never commit API keys to version control
- **Rotate regularly** - Create new keys periodically and revoke old ones
- **Minimum scopes** - Only request the permissions you need

**Key Format**

Flare API keys use the format \`flr_pat_...\` to help you identify them in your code and logs.`,
        examples: {
          curl: `# Store your API key in an environment variable
export FLARE_API_KEY="flr_pat_..."

# Use the variable in requests
curl https://flare.link/api/videos \\
  -H "Authorization: Bearer $FLARE_API_KEY"`,
          node: `// Load API key from environment
const FLARE_API_KEY = process.env.FLARE_API_KEY;

// Create a reusable client
async function flareRequest(endpoint, options = {}) {
  const res = await fetch(\`https://flare.link\${endpoint}\`, {
    ...options,
    headers: {
      Authorization: \`Bearer \${FLARE_API_KEY}\`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  return res.json();
}

// List all videos
const { videos } = await flareRequest("/api/videos");`,
          python: `import os
import requests

# Load API key from environment
FLARE_API_KEY = os.environ.get("FLARE_API_KEY")

# Create a session with default headers
session = requests.Session()
session.headers.update({
    "Authorization": f"Bearer {FLARE_API_KEY}",
    "Content-Type": "application/json"
})

# List all videos
res = session.get("https://flare.link/api/videos")
videos = res.json()["videos"]`,
        },
        response: `{
  "success": true,
  "videos": [
    {
      "id": "vid_abc123",
      "fileName": "product-demo.mp4",
      "duration": 120,
      "createdAt": "2025-01-15T10:30:00.000Z"
    }
  ]
}`,
        method: "GET",
        path: "/api/videos",
        scopes: ["videos:read"],
      },
    ],
  },
  {
    id: "videos",
    title: "Videos",
    articles: [
      {
        id: "list-videos",
        title: "List videos",
        method: "GET",
        path: "/api/videos",
        scopes: ["videos:read"],
        description: "List all videos in the authenticated account.",
        examples: {
          curl: `curl https://flare.link/api/videos \\
  -H "Authorization: Bearer flr_pat_..."`,
          node: `const res = await fetch("https://flare.link/api/videos", {
  headers: { Authorization: "Bearer flr_pat_..." }
});
const data = await res.json();
console.log(data.videos.length);`,
          python: `import requests

res = requests.get(
  "https://flare.link/api/videos",
  headers={"Authorization": "Bearer flr_pat_..."}
)
print(res.json()["videos"])`,
          ruby: `require "net/http"

uri = URI("https://flare.link/api/videos")
req = Net::HTTP::Get.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "fmt"
  "io"
  "net/http"
)

func main() {
  req, _ := http.NewRequest("GET", "https://flare.link/api/videos", nil)
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$ch = curl_init("https://flare.link/api/videos");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer flr_pat_..."]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var res = await client.GetAsync("https://flare.link/api/videos");
var body = await res.Content.ReadAsStringAsync();`,
        },
        response: `{
  "success": true,
  "videos": [
    {
      "id": "vid_demo_123",
      "fileName": "demo.mp4",
      "duration": 120,
      "createdAt": "2025-01-01T12:00:00.000Z"
    }
  ]
}`,
      },
      {
        id: "get-video",
        title: "Get a video",
        method: "GET",
        path: "/api/video/:id",
        scopes: ["videos:read"],
        description: "Fetch a single video record. Use editor headers to access transcript and waveform fields.",
        examples: {
          curl: `curl https://flare.link/api/video/VIDEO_ID \\
  -H "Authorization: Bearer flr_pat_..." \\
  -H "X-Request-Context: editor"`,
          node: `const res = await fetch("https://flare.link/api/video/VIDEO_ID", {
  headers: {
    Authorization: "Bearer flr_pat_...",
    "X-Request-Context": "editor"
  }
});
const data = await res.json();`,
          python: `import requests

res = requests.get(
  "https://flare.link/api/video/VIDEO_ID",
  headers={
    "Authorization": "Bearer flr_pat_...",
    "X-Request-Context": "editor"
  }
)
print(res.json())`,
          ruby: `require "net/http"

uri = URI("https://flare.link/api/video/VIDEO_ID")
req = Net::HTTP::Get.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
req["X-Request-Context"] = "editor"
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "fmt"
  "io"
  "net/http"
)

func main() {
  req, _ := http.NewRequest("GET", "https://flare.link/api/video/VIDEO_ID", nil)
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  req.Header.Set("X-Request-Context", "editor")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$ch = curl_init("https://flare.link/api/video/VIDEO_ID");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer flr_pat_...",
  "X-Request-Context: editor"
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
client.DefaultRequestHeaders.Add("X-Request-Context", "editor");
var res = await client.GetAsync("https://flare.link/api/video/VIDEO_ID");
var body = await res.Content.ReadAsStringAsync();`,
        },
        response: `{
  "success": true,
  "video": {
    "id": "VIDEO_ID",
    "fileName": "demo.mp4",
    "uploadStatus": "ready"
  }
}`,
      },
      {
        id: "create-video",
        title: "Create a video",
        method: "POST",
        path: "/api/video/create",
        scopes: ["videos:write"],
        description: "Create a video record before uploading media.",
        examples: {
          curl: `curl https://flare.link/api/video/create \\
  -H "Authorization: Bearer flr_pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "fileName": "demo.mp4",
    "s3Key": "vid_demo_123",
    "source": "composite",
    "width": 1920,
    "height": 1080,
    "duration": 120
  }'`,
          node: `const res = await fetch("https://flare.link/api/video/create", {
  method: "POST",
  headers: {
    Authorization: "Bearer flr_pat_...",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    fileName: "demo.mp4",
    s3Key: "vid_demo_123",
    source: "composite",
    width: 1920,
    height: 1080,
    duration: 120
  })
});
const data = await res.json();`,
          python: `import requests

payload = {
  "fileName": "demo.mp4",
  "s3Key": "vid_demo_123",
  "source": "composite"
}
res = requests.post(
  "https://flare.link/api/video/create",
  headers={"Authorization": "Bearer flr_pat_..."},
  json=payload
)
print(res.json())`,
          ruby: `require "net/http"
require "json"

uri = URI("https://flare.link/api/video/create")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
req["Content-Type"] = "application/json"
req.body = { fileName: "demo.mp4", s3Key: "vid_demo_123", source: "composite" }.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "bytes"
  "fmt"
  "io"
  "net/http"
)

func main() {
  payload := []byte(\`{"fileName":"demo.mp4","s3Key":"vid_demo_123","source":"composite"}\`)
  req, _ := http.NewRequest("POST", "https://flare.link/api/video/create", bytes.NewBuffer(payload))
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  req.Header.Set("Content-Type", "application/json")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$payload = json_encode([
  "fileName" => "demo.mp4",
  "s3Key" => "vid_demo_123",
  "source" => "composite"
]);
$ch = curl_init("https://flare.link/api/video/create");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer flr_pat_...",
  "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var payload = JsonSerializer.Serialize(new { fileName = "demo.mp4", s3Key = "vid_demo_123", source = "composite" });
var res = await client.PostAsync("https://flare.link/api/video/create", new StringContent(payload, Encoding.UTF8, "application/json"));
var body = await res.Content.ReadAsStringAsync();`,
        },
        response: `{
  "success": true,
  "video": {
    "id": "vid_demo_123",
    "uploadStatus": "uploading"
  }
}`,
      },
      {
        id: "update-video",
        title: "Update video metadata",
        method: "PATCH",
        path: "/api/video/update/:id",
        scopes: ["videos:write"],
        description: `Update video title or password protection. Available fields:

- **title** (string) - Display name for the video
- **password** (string | null) - Set a password to protect the video, or null to remove protection`,
        examples: {
          curl: `curl https://flare.link/api/video/update/VIDEO_ID \\
  -H "Authorization: Bearer flr_pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Launch walkthrough",
    "password": "secret123"
  }'`,
          node: `await fetch("https://flare.link/api/video/update/VIDEO_ID", {
  method: "PATCH",
  headers: {
    Authorization: "Bearer flr_pat_...",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    title: "Launch walkthrough",
    password: "secret123"  // or null to remove
  })
});`,
          python: `import requests

res = requests.patch(
  "https://flare.link/api/video/update/VIDEO_ID",
  headers={"Authorization": "Bearer flr_pat_..."},
  json={
    "title": "Launch walkthrough",
    "password": "secret123"  # or None to remove
  }
)
print(res.json())`,
          ruby: `require "net/http"
require "json"

uri = URI("https://flare.link/api/video/update/VIDEO_ID")
req = Net::HTTP::Patch.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
req["Content-Type"] = "application/json"
req.body = {
  title: "Launch walkthrough",
  password: "secret123"  # or nil to remove
}.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "bytes"
  "fmt"
  "io"
  "net/http"
)

func main() {
  payload := []byte(\`{"title":"Launch walkthrough","password":"secret123"}\`)
  req, _ := http.NewRequest("PATCH", "https://flare.link/api/video/update/VIDEO_ID", bytes.NewBuffer(payload))
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  req.Header.Set("Content-Type", "application/json")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$payload = json_encode([
  "title" => "Launch walkthrough",
  "password" => "secret123"  // or null to remove
]);
$ch = curl_init("https://flare.link/api/video/update/VIDEO_ID");
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "PATCH");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer flr_pat_...",
  "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var payload = JsonSerializer.Serialize(new {
  title = "Launch walkthrough",
  password = "secret123"  // or null to remove
});
var res = await client.PatchAsync("https://flare.link/api/video/update/VIDEO_ID", new StringContent(payload, Encoding.UTF8, "application/json"));`,
        },
        response: `{
  "success": true,
  "video": {
    "id": "VIDEO_ID",
    "title": "Launch walkthrough",
    "isPasswordProtected": true
  }
}`,
      },
      {
        id: "delete-video",
        title: "Delete a video",
        method: "DELETE",
        path: "/api/videos/:id",
        scopes: ["videos:write"],
        description: "Delete a video and its assets.",
        examples: {
          curl: `curl -X DELETE https://flare.link/api/videos/VIDEO_ID \\
  -H "Authorization: Bearer flr_pat_..."`,
          node: `await fetch("https://flare.link/api/videos/VIDEO_ID", {
  method: "DELETE",
  headers: { Authorization: "Bearer flr_pat_..." }
});`,
          python: `import requests

res = requests.delete(
  "https://flare.link/api/videos/VIDEO_ID",
  headers={"Authorization": "Bearer flr_pat_..."}
)
print(res.json())`,
          ruby: `require "net/http"

uri = URI("https://flare.link/api/videos/VIDEO_ID")
req = Net::HTTP::Delete.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "fmt"
  "io"
  "net/http"
)

func main() {
  req, _ := http.NewRequest("DELETE", "https://flare.link/api/videos/VIDEO_ID", nil)
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$ch = curl_init("https://flare.link/api/videos/VIDEO_ID");
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "DELETE");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer flr_pat_..."]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var res = await client.DeleteAsync("https://flare.link/api/videos/VIDEO_ID");`,
        },
        response: `{
  "success": true
}`,
      },
    ],
  },
  {
    id: "analytics",
    title: "Analytics",
    articles: [
      {
        id: "get-video-analytics",
        title: "Get video analytics",
        method: "GET",
        path: "/api/video/:id/analytics",
        scopes: ["videos:read"],
        description: `Fetch view count, watch time, and engagement metrics for a video. Query parameters:

- **period** (string) - Time range: "7d", "30d", "90d", or "all" (default: "30d")
- **timezone** (string) - IANA timezone for date grouping (default: "UTC")`,
        examples: {
          curl: `curl "https://flare.link/api/video/VIDEO_ID/analytics?period=30d" \\
  -H "Authorization: Bearer flr_pat_..."`,
          node: `const res = await fetch(
  "https://flare.link/api/video/VIDEO_ID/analytics?period=30d",
  { headers: { Authorization: "Bearer flr_pat_..." } }
);
const data = await res.json();
console.log(data.analytics);`,
          python: `import requests

res = requests.get(
  "https://flare.link/api/video/VIDEO_ID/analytics",
  headers={"Authorization": "Bearer flr_pat_..."},
  params={"period": "30d"}
)
print(res.json())`,
          ruby: `require "net/http"

uri = URI("https://flare.link/api/video/VIDEO_ID/analytics?period=30d")
req = Net::HTTP::Get.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "fmt"
  "io"
  "net/http"
)

func main() {
  req, _ := http.NewRequest("GET", "https://flare.link/api/video/VIDEO_ID/analytics?period=30d", nil)
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$ch = curl_init("https://flare.link/api/video/VIDEO_ID/analytics?period=30d");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer flr_pat_..."]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var res = await client.GetAsync("https://flare.link/api/video/VIDEO_ID/analytics?period=30d");
var body = await res.Content.ReadAsStringAsync();`,
        },
        response: `{
  "success": true,
  "analytics": {
    "videoId": "VIDEO_ID",
    "period": "30d",
    "totalViews": 1542,
    "uniqueViewers": 892,
    "totalWatchTimeSeconds": 48250,
    "averageWatchPercent": 68.4,
    "completionRate": 42.1,
    "dailyViews": [
      { "date": "2025-01-01", "views": 45 },
      { "date": "2025-01-02", "views": 62 }
    ]
  }
}`,
      },
    ],
  },
  {
    id: "uploads",
    title: "Uploads",
    articles: [
      {
        id: "presign-upload",
        title: "Presign an upload",
        method: "POST",
        path: "/api/video/upload/presign",
        scopes: ["uploads:write"],
        description: "Generate a signed URL for direct upload to storage.",
        examples: {
          curl: `curl https://flare.link/api/video/upload/presign \\
  -H "Authorization: Bearer flr_pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "videoId": "VIDEO_ID",
    "fileName": "demo.mp4"
  }'`,
          node: `const res = await fetch("https://flare.link/api/video/upload/presign", {
  method: "POST",
  headers: {
    Authorization: "Bearer flr_pat_...",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ videoId: "VIDEO_ID", fileName: "demo.mp4" })
});
const data = await res.json();`,
          python: `import requests

res = requests.post(
  "https://flare.link/api/video/upload/presign",
  headers={"Authorization": "Bearer flr_pat_..."},
  json={ "videoId": "VIDEO_ID", "fileName": "demo.mp4" }
)
print(res.json())`,
          ruby: `require "net/http"
require "json"

uri = URI("https://flare.link/api/video/upload/presign")
req = Net::HTTP::Post.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
req["Content-Type"] = "application/json"
req.body = { videoId: "VIDEO_ID", fileName: "demo.mp4" }.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "bytes"
  "fmt"
  "io"
  "net/http"
)

func main() {
  payload := []byte(\`{"videoId":"VIDEO_ID","fileName":"demo.mp4"}\`)
  req, _ := http.NewRequest("POST", "https://flare.link/api/video/upload/presign", bytes.NewBuffer(payload))
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  req.Header.Set("Content-Type", "application/json")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$payload = json_encode(["videoId" => "VIDEO_ID", "fileName" => "demo.mp4"]);
$ch = curl_init("https://flare.link/api/video/upload/presign");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer flr_pat_...",
  "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var payload = JsonSerializer.Serialize(new { videoId = "VIDEO_ID", fileName = "demo.mp4" });
var res = await client.PostAsync("https://flare.link/api/video/upload/presign", new StringContent(payload, Encoding.UTF8, "application/json"));`,
        },
        response: `{
  "uploadUrl": "https://signed-upload-url",
  "method": "PUT",
  "headers": { "Content-Type": "video/mp4" },
  "publicUrl": "https://videos.flare.link/VIDEO_ID/composite/demo.mp4"
}`,
      },
    ],
  },
  {
    id: "embeds",
    title: "Embeds",
    articles: [
      {
        id: "update-embed-settings",
        title: "Update embed settings",
        method: "PATCH",
        path: "/api/video/:id/embed-settings",
        scopes: ["embeds:write"],
        description: `Customize player appearance and behavior. Available options:

**Theme & Appearance**
- **playerTheme** ("flare" | "minimal" | "custom") - Player visual style
- **accentColor** (string) - Hex color for controls, e.g. "#FF6702"
- **embedBorderRadius** (number) - Corner radius in pixels (0-24)
- **thumbnailTime** (number) - Timestamp in seconds for poster frame

**Controls**
- **controlOptions.showPlayButton** (boolean) - Show center play button
- **controlOptions.showProgress** (boolean) - Show progress bar
- **controlOptions.showTime** (boolean) - Show current/total time
- **controlOptions.showVolume** (boolean) - Show volume control
- **controlOptions.showSpeed** (boolean) - Show playback speed menu
- **controlOptions.showFullscreen** (boolean) - Show fullscreen button
- **controlOptions.showCaptions** (boolean) - Show captions toggle

**Behavior**
- **autoplay** (boolean) - Start playing automatically (muted)
- **loop** (boolean) - Loop video when it ends
- **muted** (boolean) - Start muted
- **showBranding** (boolean) - Show Flare branding
- **allowDownload** (boolean) - Allow viewers to download`,
        examples: {
          curl: `curl https://flare.link/api/video/VIDEO_ID/embed-settings \\
  -H "Authorization: Bearer flr_pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "embedSettings": {
      "playerTheme": "flare",
      "accentColor": "#FF6702",
      "embedBorderRadius": 12,
      "autoplay": false,
      "loop": false,
      "showBranding": true,
      "allowDownload": false,
      "controlOptions": {
        "showPlayButton": true,
        "showProgress": true,
        "showTime": true,
        "showVolume": true,
        "showSpeed": true,
        "showFullscreen": true,
        "showCaptions": true
      }
    }
  }'`,
          node: `await fetch("https://flare.link/api/video/VIDEO_ID/embed-settings", {
  method: "PATCH",
  headers: {
    Authorization: "Bearer flr_pat_...",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    embedSettings: {
      playerTheme: "flare",
      accentColor: "#FF6702",
      embedBorderRadius: 12,
      autoplay: false,
      showBranding: true,
      controlOptions: {
        showSpeed: true,
        showCaptions: true
      }
    }
  })
});`,
          python: `import requests

payload = {
  "embedSettings": {
    "playerTheme": "flare",
    "accentColor": "#FF6702",
    "embedBorderRadius": 12,
    "autoplay": False,
    "showBranding": True,
    "controlOptions": {
      "showSpeed": True,
      "showCaptions": True
    }
  }
}
res = requests.patch(
  "https://flare.link/api/video/VIDEO_ID/embed-settings",
  headers={"Authorization": "Bearer flr_pat_..."},
  json=payload
)
print(res.json())`,
          ruby: `require "net/http"
require "json"

uri = URI("https://flare.link/api/video/VIDEO_ID/embed-settings")
req = Net::HTTP::Patch.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
req["Content-Type"] = "application/json"
req.body = {
  embedSettings: {
    playerTheme: "flare",
    accentColor: "#FF6702",
    embedBorderRadius: 12,
    autoplay: false,
    showBranding: true,
    controlOptions: { showSpeed: true, showCaptions: true }
  }
}.to_json
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "bytes"
  "fmt"
  "io"
  "net/http"
)

func main() {
  payload := []byte(\`{
    "embedSettings": {
      "playerTheme": "flare",
      "accentColor": "#FF6702",
      "embedBorderRadius": 12,
      "controlOptions": { "showSpeed": true }
    }
  }\`)
  req, _ := http.NewRequest("PATCH", "https://flare.link/api/video/VIDEO_ID/embed-settings", bytes.NewBuffer(payload))
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  req.Header.Set("Content-Type", "application/json")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$payload = json_encode([
  "embedSettings" => [
    "playerTheme" => "flare",
    "accentColor" => "#FF6702",
    "embedBorderRadius" => 12,
    "autoplay" => false,
    "showBranding" => true,
    "controlOptions" => [
      "showSpeed" => true,
      "showCaptions" => true
    ]
  ]
]);
$ch = curl_init("https://flare.link/api/video/VIDEO_ID/embed-settings");
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "PATCH");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer flr_pat_...",
  "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, $payload);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var payload = JsonSerializer.Serialize(new {
  embedSettings = new {
    playerTheme = "flare",
    accentColor = "#FF6702",
    embedBorderRadius = 12,
    autoplay = false,
    showBranding = true,
    controlOptions = new { showSpeed = true, showCaptions = true }
  }
});
var res = await client.PatchAsync("https://flare.link/api/video/VIDEO_ID/embed-settings", new StringContent(payload, Encoding.UTF8, "application/json"));`,
        },
        response: `{
  "success": true,
  "video": {
    "id": "VIDEO_ID",
    "embedSettings": {
      "playerTheme": "flare",
      "accentColor": "#FF6702",
      "embedBorderRadius": 12,
      "autoplay": false,
      "showBranding": true
    }
  }
}`,
      },
      {
        id: "get-embed-code",
        title: "Get embed code",
        method: "GET",
        path: "/api/video/:id/embed",
        scopes: ["embeds:write"],
        description: "Generate HTML embed code or iframe URL for a video.",
        examples: {
          curl: `curl "https://flare.link/api/video/VIDEO_ID/embed?format=iframe" \\
  -H "Authorization: Bearer flr_pat_..."`,
          node: `const res = await fetch(
  "https://flare.link/api/video/VIDEO_ID/embed?format=iframe",
  { headers: { Authorization: "Bearer flr_pat_..." } }
);
const { embedUrl, embedCode } = await res.json();`,
          python: `import requests

res = requests.get(
  "https://flare.link/api/video/VIDEO_ID/embed",
  headers={"Authorization": "Bearer flr_pat_..."},
  params={"format": "iframe"}
)
print(res.json()["embedCode"])`,
          ruby: `require "net/http"

uri = URI("https://flare.link/api/video/VIDEO_ID/embed?format=iframe")
req = Net::HTTP::Get.new(uri)
req["Authorization"] = "Bearer flr_pat_..."
res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
puts res.body`,
          go: `package main

import (
  "fmt"
  "io"
  "net/http"
)

func main() {
  req, _ := http.NewRequest("GET", "https://flare.link/api/video/VIDEO_ID/embed?format=iframe", nil)
  req.Header.Set("Authorization", "Bearer flr_pat_...")
  resp, _ := http.DefaultClient.Do(req)
  body, _ := io.ReadAll(resp.Body)
  fmt.Println(string(body))
}`,
          php: `<?php
$ch = curl_init("https://flare.link/api/video/VIDEO_ID/embed?format=iframe");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer flr_pat_..."]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`,
          csharp: `using System.Net.Http.Headers;

using var client = new HttpClient();
client.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", "flr_pat_...");
var res = await client.GetAsync("https://flare.link/api/video/VIDEO_ID/embed?format=iframe");
var body = await res.Content.ReadAsStringAsync();`,
        },
        response: `{
  "success": true,
  "embedUrl": "https://flare.link/embed/VIDEO_ID",
  "embedCode": "<iframe src=\\"https://flare.link/embed/VIDEO_ID\\" width=\\"100%\\" height=\\"auto\\" frameborder=\\"0\\" allowfullscreen></iframe>"
}`,
      },
    ],
  },
];
